'use client';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import Button from '@/components/ui/Button';
import { Select } from '@/components/ui/Select/Select';
import { TextArea } from '@/components/ui/TextAreaModal/TextAreaModal';
import LoadingDots from '@/components/ui/LoadingDots';

interface Props {
  trigger: ReactNode;
  toneId: number;
  toneTitle: string;
  toneAuthor: string;
}

const reportReasons = [
  { value: 'copyright', label: 'Copyright or intellectual property violation' },
  { value: 'inappropriate', label: 'Inappropriate or offensive content' },
  { value: 'spam', label: 'Spam or misleading content' },
  { value: 'mislabeled_gear', label: 'Mislabeled gear' },
  { value: 'ai_generated', label: 'AI-generated images or content' },
  { value: 'hate_speech', label: 'Hate speech or discrimination' },
  { value: 'other', label: 'Other (please specify in details)' },
];

export const ReportToneModal = ({ trigger, toneId, toneTitle, toneAuthor }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const [selectedReason, setSelectedReason] = useState(reportReasons[0].value);
  const [details, setDetails] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const toggleModal = () => {
    if (isOpen) {
      // Reset form when closing
      setSelectedReason(reportReasons[0].value);
      setDetails('');
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        toggleModal();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.body.style.overflow = 'auto';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/tones/report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          toneId,
          reason: reportReasons.find((r) => r.value === selectedReason)?.label || 'Other',
          details: details.trim() || null,
        }),
      });

      if (response.ok) {
        // Close modal immediately after successful submission
        toggleModal();
      } else {
        console.error('Failed to submit report');
      }
    } catch (error) {
      console.error('Error submitting report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button onClick={toggleModal} className="contents">
        {trigger}
      </button>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-40">
          <div
            ref={modalRef}
            className="bg-zinc-900 border border-zinc-500 border-opacity-50 text-white w-full max-w-md rounded-xl shadow-xl flex flex-col"
          >
            <div className="flex justify-between items-center p-4 border-zinc-700">
              <span className="text-xl font-semibold">Report Tone</span>
              <button onClick={toggleModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="p-4 flex flex-col gap-6">
              <div className="bg-zinc-800 p-3 rounded-lg">
                <p className="text-sm text-zinc-400 mb-1">Reporting tone by {toneAuthor}:</p>
                <p className="text-sm italic">"{toneTitle}"</p>
              </div>

              <Select
                label="Reason for reporting"
                options={reportReasons}
                value={selectedReason}
                onChange={(value) => setSelectedReason(value as string)}
              />

              <div className="flex flex-col gap-1">
                <label className="text-sm text-zinc-400" htmlFor="report-details">
                  Additional details (optional)
                </label>
                <TextArea
                  text={details}
                  setText={setDetails}
                  placeholder="Optional: provide additional details about why you're reporting this tone..."
                  maxLength={1000}
                  name="report-details"
                />
                <p className="text-xs text-zinc-500">{details.length}/1000 characters</p>
              </div>

              <div className="flex flex-row gap-4">
                <Button
                  className="w-full text-base"
                  onClick={toggleModal}
                  isSecondary={true}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  className="w-full text-base"
                  onClick={handleSubmit}
                  isPrimary={true}
                  disabled={isLoading}
                >
                  {isLoading && <LoadingDots />}
                  {!isLoading && 'Submit'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
